/**
 * Intelligent Cart Upsell System
 * Provides personalized product recommendations based on:
 * - Cart contents
 * - Recently viewed products
 * - Search history
 * - Purchase history
 * - Product relationships
 */

class IntelligentCartUpsell {
  constructor() {
    this.storageKeys = {
      recentlyViewed: 'recently_viewed_products_fallback',
      searchHistory: 'search_history',
      cartHistory: 'cart_history'
    };
    
    this.weights = {
      cartRelated: 0.40,      // 40% - Products related to cart items
      recentlyViewed: 0.25,   // 25% - Recently browsed products
      searchBased: 0.15,      // 15% - Based on search queries
      trending: 0.10,         // 10% - Popular/bestsellers
      complementary: 0.10     // 10% - Complementary products
    };
    
    this.maxRecommendations = 8;
    this.debug = false;
    
    // Performance optimizations
    this.cache = new Map();
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
    this.requestQueue = new Map();
    this.debounceTimers = new Map();
    this.intersectionObserver = null;
    
    // Batch processing
    this.batchSize = 3;
    this.processingQueue = [];
    this.isProcessing = false;
    
    this.init();
  }

  init() {
    if (this.debug) console.log('Intelligent Upsell: Initializing...');
    
    // Performance: Use requestIdleCallback for non-critical initialization
    this.scheduleIdleWork(() => {
      this.trackSearches();
      this.trackCartChanges();
    });
    
    // Initialize sections with intersection observer for lazy loading
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeIntersectionObserver();
        this.initializeSections();
      });
    } else {
      this.initializeIntersectionObserver();
      this.initializeSections();
    }
    
    // Debounced cart update listener
    document.addEventListener('cart:updated', this.debounce(() => {
      this.refreshRecommendations();
    }, 300));
  }

  /**
   * Schedule work during idle time to avoid blocking main thread
   */
  scheduleIdleWork(callback) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback, { timeout: 2000 });
    } else {
      setTimeout(callback, 100);
    }
  }

  /**
   * Debounce function to prevent excessive calls
   */
  debounce(func, wait) {
    return (...args) => {
      const key = func.name || 'anonymous';
      clearTimeout(this.debounceTimers.get(key));
      this.debounceTimers.set(key, setTimeout(() => func.apply(this, args), wait));
    };
  }

  /**
   * Initialize intersection observer for lazy loading
   */
  initializeIntersectionObserver() {
    if (!('IntersectionObserver' in window)) return;
    
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.initializeSection(entry.target);
          this.intersectionObserver.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '50px 0px',
      threshold: 0.1
    });
  }

  /**
   * Track search queries for intelligence
   */
  trackSearches() {
    const searchInputs = document.querySelectorAll('input[type="search"], input[name="q"]');
    searchInputs.forEach(input => {
      input.addEventListener('input', (e) => {
        const query = e.target.value.trim();
        if (query.length > 2) {
          this.saveSearchQuery(query);
        }
      });
    });
  }

  /**
   * Save search query to history
   */
  saveSearchQuery(query) {
    try {
      const history = this.getSearchHistory();
      const timestamp = Date.now();
      
      // Add new query
      history.unshift({ query, timestamp });
      
      // Keep last 20 searches
      const limited = history.slice(0, 20);
      
      localStorage.setItem(this.storageKeys.searchHistory, JSON.stringify(limited));
    } catch (error) {
      if (this.debug) console.warn('Failed to save search query:', error);
    }
  }

  /**
   * Get search history
   */
  getSearchHistory() {
    try {
      const data = localStorage.getItem(this.storageKeys.searchHistory);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Track cart changes for pattern analysis
   */
  trackCartChanges() {
    // Listen for cart updates
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      
      // Check if it's a cart update
      if (args[0] && (args[0].includes('/cart') || args[0].includes('cart.js'))) {
        setTimeout(() => this.analyzeCart(), 500);
      }
      
      return response;
    };
  }

  /**
   * Analyze current cart and save patterns
   */
  async analyzeCart() {
    try {
      const cart = await this.getCurrentCart();
      if (!cart || !cart.items || cart.items.length === 0) return;
      
      const cartData = {
        items: cart.items.map(item => ({
          id: item.product_id,
          handle: item.handle,
          title: item.product_title,
          vendor: item.vendor,
          tags: item.product_tags || [],
          timestamp: Date.now()
        })),
        total: cart.total_price,
        timestamp: Date.now()
      };
      
      this.saveCartHistory(cartData);
    } catch (error) {
      if (this.debug) console.warn('Failed to analyze cart:', error);
    }
  }

  /**
   * Get current cart from Shopify
   */
  async getCurrentCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      if (this.debug) console.warn('Failed to fetch cart:', error);
      return null;
    }
  }

  /**
   * Save cart history for pattern learning
   */
  saveCartHistory(cartData) {
    try {
      const history = this.getCartHistory();
      history.unshift(cartData);
      
      // Keep last 10 cart states
      const limited = history.slice(0, 10);
      
      localStorage.setItem(this.storageKeys.cartHistory, JSON.stringify(limited));
    } catch (error) {
      if (this.debug) console.warn('Failed to save cart history:', error);
    }
  }

  /**
   * Get cart history
   */
  getCartHistory() {
    try {
      const data = localStorage.getItem(this.storageKeys.cartHistory);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Initialize upsell sections on the page with lazy loading
   */
  initializeSections() {
    const sections = document.querySelectorAll('[data-intelligent-upsell]');
    if (this.debug) console.log('Intelligent Upsell: Found sections:', sections.length);
    
    if (this.intersectionObserver) {
      // Use intersection observer for lazy loading
      sections.forEach(section => {
        if (!section.dataset.initialized) {
          this.intersectionObserver.observe(section);
        }
      });
    } else {
      // Fallback: initialize immediately but with batching
      this.batchProcessSections(Array.from(sections));
    }
  }

  /**
   * Process sections in batches to avoid blocking the main thread
   */
  async batchProcessSections(sections) {
    if (this.isProcessing) return;
    this.isProcessing = true;

    for (let i = 0; i < sections.length; i += this.batchSize) {
      const batch = sections.slice(i, i + this.batchSize);
      
      // Process batch
      await Promise.all(batch.map(section => this.initializeSection(section)));
      
      // Yield control to browser between batches
      if (i + this.batchSize < sections.length) {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }

    this.isProcessing = false;
  }

  /**
   * Initialize a single upsell section with caching and performance optimizations
   */
  async initializeSection(section) {
    try {
      // Prevent duplicate initialization
      if (section.dataset.initialized) return;
      section.dataset.initialized = 'true';
      
      if (this.debug) console.log('Intelligent Upsell: Initializing section');
      
      // Get the max items setting from the section
      const maxItems = parseInt(section.dataset.maxItems) || this.maxRecommendations;
      const cacheKey = `upsell_${maxItems}_${Date.now() - (Date.now() % this.cacheExpiry)}`;
      
      // Check cache first
      if (this.cache.has(cacheKey)) {
        const cachedRecommendations = this.cache.get(cacheKey);
        if (this.debug) console.log('Intelligent Upsell: Using cached recommendations');
        await this.renderRecommendations(section, cachedRecommendations);
        return;
      }
      
      // Show loading state
      this.showLoadingState(section);
      
      // Get intelligent recommendations with section-specific limit
      const recommendations = await this.getIntelligentRecommendations(maxItems);
      
      // Cache the results
      this.cache.set(cacheKey, recommendations);
      
      if (this.debug) console.log('Intelligent Upsell: Recommendations:', recommendations);
      
      if (recommendations.length === 0) {
        this.showEmptyState(section);
        return;
      }
      
      // Render recommendations using document fragment for better performance
      await this.renderRecommendations(section, recommendations);
      
    } catch (error) {
      console.warn('Intelligent Upsell: Failed to initialize section', error);
      this.showEmptyState(section);
    }
  }

  /**
   * Get intelligent product recommendations
   * This is the core intelligence engine
   */
  async getIntelligentRecommendations(maxItems = this.maxRecommendations) {
    try {
      // Gather all intelligence sources
      const cart = await this.getCurrentCart();
      const recentlyViewed = this.getRecentlyViewed();
      const searchHistory = this.getSearchHistory();
      const cartHistory = this.getCartHistory();
      
      if (this.debug) {
        console.log('Intelligence Sources:', {
          cartItems: cart?.items?.length || 0,
          recentlyViewed: recentlyViewed.length,
          searches: searchHistory.length,
          cartHistory: cartHistory.length,
          maxItems: maxItems
        });
      }
      
      // Score all potential products
      const scoredProducts = await this.scoreProducts({
        cart,
        recentlyViewed,
        searchHistory,
        cartHistory
      });
      
      // Sort by score and return top recommendations (using section-specific limit)
      const sorted = scoredProducts.sort((a, b) => b.score - a.score);
      const topRecommendations = sorted.slice(0, maxItems);
      
      // Fetch full product data
      const products = await this.fetchProductsData(topRecommendations);
      
      return products;
      
    } catch (error) {
      console.warn('Intelligent Upsell: Failed to get recommendations', error);
      return [];
    }
  }

  /**
   * Score products based on multiple intelligence signals
   * This is where the magic happens!
   */
  async scoreProducts(sources) {
    const { cart, recentlyViewed, searchHistory, cartHistory } = sources;
    const productScores = new Map();
    
    // Get cart item IDs to exclude
    const cartItemIds = cart?.items?.map(item => item.product_id) || [];
    
    // 1. CART-RELATED SCORING (40% weight)
    if (cart && cart.items && cart.items.length > 0) {
      await this.scoreCartRelated(cart.items, productScores, this.weights.cartRelated);
    }
    
    // 2. RECENTLY VIEWED SCORING (25% weight)
    if (recentlyViewed.length > 0) {
      this.scoreRecentlyViewed(recentlyViewed, productScores, this.weights.recentlyViewed, cartItemIds);
    }
    
    // 3. SEARCH-BASED SCORING (15% weight)
    if (searchHistory.length > 0) {
      await this.scoreSearchBased(searchHistory, productScores, this.weights.searchBased, cartItemIds);
    }
    
    // 4. TRENDING/POPULAR SCORING (10% weight)
    await this.scoreTrending(productScores, this.weights.trending, cartItemIds);
    
    // 5. COMPLEMENTARY PRODUCTS (10% weight)
    if (cart && cart.items && cart.items.length > 0) {
      await this.scoreComplementary(cart.items, productScores, this.weights.complementary, cartItemIds);
    }
    
    // Convert map to array with scores
    const scored = Array.from(productScores.entries()).map(([handle, data]) => ({
      handle,
      score: data.score,
      reasons: data.reasons
    }));
    
    return scored;
  }

  /**
   * Score products related to cart items (same vendor, tags, type)
   */
  async scoreCartRelated(cartItems, productScores, weight) {
    for (const item of cartItems) {
      // Extract product attributes
      const vendor = item.vendor;
      const tags = item.product_tags || [];
      const type = item.product_type;
      
      // Find products with matching attributes
      // In a real implementation, you'd query your product catalog
      // For now, we'll use recently viewed as a proxy
      const recentlyViewed = this.getRecentlyViewed();
      
      recentlyViewed.forEach(product => {
        if (product.id === item.product_id) return; // Skip same product
        
        const handle = product.handle;
        if (!productScores.has(handle)) {
          productScores.set(handle, { score: 0, reasons: [] });
        }
        
        const data = productScores.get(handle);
        data.score += weight;
        data.reasons.push('Related to cart items');
      });
    }
  }

  /**
   * Score recently viewed products
   */
  scoreRecentlyViewed(recentlyViewed, productScores, weight, excludeIds) {
    // More recent views get higher scores
    recentlyViewed.forEach((product, index) => {
      if (excludeIds.includes(product.id)) return; // Skip if in cart
      
      const handle = product.handle;
      if (!productScores.has(handle)) {
        productScores.set(handle, { score: 0, reasons: [] });
      }
      
      const data = productScores.get(handle);
      // Decay score based on recency (newer = higher score)
      const recencyMultiplier = 1 - (index / recentlyViewed.length) * 0.5;
      data.score += weight * recencyMultiplier;
      data.reasons.push('Recently viewed');
    });
  }

  /**
   * Score products based on search history
   */
  async scoreSearchBased(searchHistory, productScores, weight, excludeIds) {
    // Extract keywords from recent searches
    const keywords = searchHistory
      .slice(0, 5) // Last 5 searches
      .map(s => s.query.toLowerCase())
      .join(' ')
      .split(' ')
      .filter(word => word.length > 3); // Filter short words
    
    if (keywords.length === 0) return;
    
    // Score recently viewed products that match search keywords
    const recentlyViewed = this.getRecentlyViewed();
    recentlyViewed.forEach(product => {
      if (excludeIds.includes(product.id)) return;
      
      const handle = product.handle;
      const handleWords = handle.split('-');
      
      // Check if any search keyword matches product handle
      const matches = keywords.filter(keyword => 
        handleWords.some(word => word.includes(keyword) || keyword.includes(word))
      );
      
      if (matches.length > 0) {
        if (!productScores.has(handle)) {
          productScores.set(handle, { score: 0, reasons: [] });
        }
        
        const data = productScores.get(handle);
        data.score += weight * (matches.length / keywords.length);
        data.reasons.push('Matches your searches');
      }
    });
  }

  /**
   * Score trending/popular products
   */
  async scoreTrending(productScores, weight, excludeIds) {
    // Get recently viewed as proxy for trending
    // In production, you'd fetch actual bestsellers from your backend
    const recentlyViewed = this.getRecentlyViewed();
    
    // Give small boost to all recently viewed products
    recentlyViewed.slice(0, 4).forEach(product => {
      if (excludeIds.includes(product.id)) return;
      
      const handle = product.handle;
      if (!productScores.has(handle)) {
        productScores.set(handle, { score: 0, reasons: [] });
      }
      
      const data = productScores.get(handle);
      data.score += weight * 0.5;
      data.reasons.push('Popular choice');
    });
  }

  /**
   * Score complementary products
   */
  async scoreComplementary(cartItems, productScores, weight, excludeIds) {
    // Analyze cart items and suggest complementary products
    // For shirts, suggest: ties, cufflinks, blazers, etc.
    
    const complementaryRules = {
      'shirt': ['tie', 'cufflink', 'blazer', 'trouser', 'belt'],
      'trouser': ['shirt', 'belt', 'shoe'],
      'blazer': ['shirt', 'trouser', 'tie'],
      'shoe': ['trouser', 'belt', 'sock']
    };
    
    const recentlyViewed = this.getRecentlyViewed();
    
    cartItems.forEach(cartItem => {
      const itemType = (cartItem.product_type || '').toLowerCase();
      const itemTitle = (cartItem.product_title || '').toLowerCase();
      
      // Find complementary product types
      let complementaryTypes = [];
      for (const [type, complements] of Object.entries(complementaryRules)) {
        if (itemType.includes(type) || itemTitle.includes(type)) {
          complementaryTypes = complements;
          break;
        }
      }
      
      if (complementaryTypes.length === 0) return;
      
      // Score products that match complementary types
      recentlyViewed.forEach(product => {
        if (excludeIds.includes(product.id)) return;
        
        const handle = product.handle.toLowerCase();
        const matchesComplement = complementaryTypes.some(type => handle.includes(type));
        
        if (matchesComplement) {
          if (!productScores.has(product.handle)) {
            productScores.set(product.handle, { score: 0, reasons: [] });
          }
          
          const data = productScores.get(product.handle);
          data.score += weight;
          data.reasons.push('Complements your cart');
        }
      });
    });
  }

  /**
   * Fetch full product data for recommendations with batching and caching
   */
  async fetchProductsData(scoredProducts) {
    const products = [];
    const batchSize = 3; // Fetch 3 products at a time
    
    // Check cache first
    const cachedProducts = new Map();
    const uncachedItems = [];
    
    scoredProducts.forEach(item => {
      const cacheKey = `product_${item.handle}`;
      if (this.cache.has(cacheKey)) {
        const cachedProduct = this.cache.get(cacheKey);
        products.push({
          ...cachedProduct,
          score: item.score,
          reasons: item.reasons
        });
      } else {
        uncachedItems.push(item);
      }
    });
    
    // Batch fetch uncached products
    for (let i = 0; i < uncachedItems.length; i += batchSize) {
      const batch = uncachedItems.slice(i, i + batchSize);
      
      const batchPromises = batch.map(async (item) => {
        try {
          // Check if request is already in progress
          const requestKey = `fetch_${item.handle}`;
          if (this.requestQueue.has(requestKey)) {
            return await this.requestQueue.get(requestKey);
          }
          
          // Create and cache the request promise
          const requestPromise = fetch(`/products/${item.handle}.js`)
            .then(response => {
              if (!response.ok) throw new Error(`HTTP ${response.status}`);
              return response.json();
            })
            .then(product => {
              const normalizedProduct = this.normalizeProductData(product);
              // Cache the product data
              this.cache.set(`product_${item.handle}`, normalizedProduct);
              return {
                ...normalizedProduct,
                score: item.score,
                reasons: item.reasons
              };
            })
            .catch(error => {
              if (this.debug) console.warn(`Failed to fetch product ${item.handle}:`, error);
              return null;
            })
            .finally(() => {
              // Remove from request queue
              this.requestQueue.delete(requestKey);
            });
          
          this.requestQueue.set(requestKey, requestPromise);
          return await requestPromise;
          
        } catch (error) {
          if (this.debug) console.warn(`Failed to fetch product ${item.handle}:`, error);
          return null;
        }
      });
      
      const batchResults = await Promise.all(batchPromises);
      products.push(...batchResults.filter(product => product !== null));
      
      // Small delay between batches to avoid overwhelming the server
      if (i + batchSize < uncachedItems.length) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
    
    return products;
  }

  /**
   * Normalize product data - FIXED to use variant prices from Shopify API
   */
  normalizeProductData(product) {
    // Get the first available variant or the first variant
    const firstAvailableVariant = product.variants?.find(v => v.available) || product.variants?.[0];
    
    // Use variant prices directly from Shopify API (already in correct format)
    const variantPrice = firstAvailableVariant?.price || product.price || 0;
    const variantComparePrice = firstAvailableVariant?.compare_at_price || product.compare_at_price || null;
    
    return {
      id: product.id,
      handle: product.handle,
      title: product.title,
      url: `/products/${product.handle}`,
      price: variantPrice,
      compare_at_price: variantComparePrice,
      available: product.available,
      featured_image: product.featured_image,
      images: product.images || [],
      variants: product.variants || [],
      options: product.options_with_values || product.options || [],
      vendor: product.vendor,
      tags: product.tags || []
    };
  }

  /**
   * Get recently viewed products
   */
  getRecentlyViewed() {
    try {
      const data = localStorage.getItem(this.storageKeys.recentlyViewed);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Render recommendations in the section using the Olevorr ol-pc card structure
   */
  async renderRecommendations(section, products) {
    const container = section.querySelector('[data-upsell-products]');
    if (!container) return;

    // Create grid using the same class as the Liquid fallback
    const grid = document.createElement('div');
    grid.className = 'intelligent-upsell__grid';

    // Generate product cards (each call returns card + ol-qa-data script as siblings)
    products.forEach(product => {
      const cardWrapper = document.createElement('div');
      cardWrapper.innerHTML = this.generateProductCard(product, section.dataset.sectionId);
      while (cardWrapper.firstChild) {
        grid.appendChild(cardWrapper.firstChild);
      }
    });

    // Single DOM update
    requestAnimationFrame(() => {
      container.innerHTML = '';
      container.appendChild(grid);

      section.style.display = 'block';
      section.classList.remove('upsell--loading');

      this.triggerLazyImages(container);
    });
  }

  /**
   * Generate product card HTML matching the Olevorr ol-pc card structure
   * (mirrors card-product-olevorr.liquid — styled by component-card-product-olevorr.css)
   */
  generateProductCard(product, sectionId) {
    const isOnSale = product.compare_at_price && product.compare_at_price > product.price;
    const priceFormatted = this.formatPrice(product.price);
    const comparePriceFormatted = product.compare_at_price ? this.formatPrice(product.compare_at_price) : '';

    const primaryImageSrc = product.featured_image
      ? (typeof product.featured_image === 'string' ? product.featured_image : product.featured_image.src || product.featured_image)
      : '';
    const secondaryImageSrc = product.images && product.images.length > 1
      ? (typeof product.images[1] === 'string' ? product.images[1] : product.images[1].src || product.images[1])
      : '';

    const primarySrcset = primaryImageSrc ? this.generateImageSrcset(primaryImageSrc) : '';
    const secondarySrcset = secondaryImageSrc ? this.generateImageSrcset(secondaryImageSrc) : '';

    const escapedTitle = this.escapeHtml(product.title);
    const isSingleDefault = product.variants && product.variants.length === 1 &&
      (product.variants[0].title === 'Default Title' || product.variants[0].option1 === 'Default Title');

    const variantsForData = (product.variants || []).map(v => ({
      id: v.id, title: v.title, option1: v.option1, option2: v.option2, option3: v.option3,
      available: v.available,
      image: v.featured_image ? (v.featured_image.src || v.featured_image) : primaryImageSrc
    }));
    const optionsForData = (product.options || []).map(o => ({ name: o.name, values: o.values }));

    /* Store on window so the modal can read it without DOM script-element queries */
    window.olProductData = window.olProductData || {};
    if (!isSingleDefault) {
      window.olProductData[String(product.id)] = {
        id: product.id, url: product.url, title: product.title,
        price: priceFormatted, image: primaryImageSrc,
        options: optionsForData, variants: variantsForData
      };
    }

    return `
      <div class="ol-pc">
        <div class="ol-pc-image">
          <a href="${product.url}">
            ${primaryImageSrc ? `
              <img
                class="ol-pc-img-primary"
                srcset="${primarySrcset}"
                src="${primaryImageSrc}"
                sizes="(min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
                alt="${escapedTitle}"
                width="800" height="1067"
                loading="lazy">
              ${secondaryImageSrc ? `
              <img
                class="ol-pc-img-secondary"
                srcset="${secondarySrcset}"
                src="${secondaryImageSrc}"
                sizes="(min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
                alt="${escapedTitle}"
                width="800" height="1067"
                loading="lazy">
              ` : ''}
            ` : ''}
          </a>
          <button
            type="button"
            class="ol-pc-wish wishlist-trigger"
            data-product-id="${product.id}"
            data-product-handle="${product.handle}"
            aria-label="Add ${escapedTitle} to wishlist">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </button>
          <button
            type="button"
            class="ol-pc-qv"
            data-product-id="${product.id}"
            data-product-title="${escapedTitle}"
            data-product-price="${priceFormatted}"
            data-product-url="${product.url}"
            ${isSingleDefault ? `data-single-variant="${product.variants[0].id}"` : ''}
            aria-label="Quick view ${escapedTitle}">Quick View</button>
        </div>
        <a href="${product.url}" class="ol-pc-name">${escapedTitle}</a>
        <div class="ol-pc-price">
          <span class="ol-price-main">${priceFormatted}</span>
          ${isOnSale ? `<span class="ol-price-was">${comparePriceFormatted}</span>` : ''}
        </div>
      </div>
      ${!isSingleDefault ? `<script type="application/json" class="ol-qa-data" data-product-id="${product.id}">${JSON.stringify({id:product.id,url:product.url,title:product.title,price:priceFormatted,image:primaryImageSrc,options:optionsForData,variants:variantsForData})}<\/script>` : ''}
    `;
  }

  /**
   * Trigger lazy loading for images in the container
   */
  triggerLazyImages(container) {
    const images = container.querySelectorAll('img[loading="lazy"]');
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            imageObserver.unobserve(img);
          }
        });
      });
      
      images.forEach(img => imageObserver.observe(img));
    }
  }

  /**
   * Generate responsive image srcset
   */
  generateImageSrcset(imageUrl) {
    if (!imageUrl) return '';
    const baseUrl = imageUrl.split('?')[0];
    const sizes = [165, 360, 533, 720, 940, 1066];
    return sizes.map(size => `${baseUrl}?width=${size} ${size}w`).join(', ') + `, ${imageUrl}`;
  }

  /**
   * Format price - Shopify API returns prices in cents, formatMoney handles conversion
   */
  formatPrice(price) {
    if (typeof price !== 'number') return '₹0.00';
    
    // Shopify.formatMoney expects price in cents and handles the division automatically
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(price);
    }
    
    // Fallback: Shopify API returns prices in cents/paise, so divide by 100
    const rupees = (price / 100).toFixed(2);
    return `₹${rupees}`;
  }

  /**
   * Escape HTML
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Show loading state
   */
  showLoadingState(section) {
    section.classList.add('upsell--loading');
    const container = section.querySelector('[data-upsell-products]');
    if (container) {
      container.innerHTML = `
        <div style="text-align: center; padding: 2rem;">
          <div style="margin: 0 auto 1rem; width: 2rem; height: 2rem; border: 2px solid #d8d0c2; border-top-color: #0c1e10; animation: spin 1s linear infinite;"></div>
          <p style="font-family: 'Didact Gothic', sans-serif; font-size: 9px; letter-spacing: 0.22em; text-transform: uppercase; color: #7a7068;">Finding perfect recommendations...</p>
        </div>
      `;
    }
  }

  /**
   * Show empty state — reveal the Liquid-rendered fallback products
   * (hides the JS spinner, shows the server-side random product grid)
   */
  showEmptyState(section) {
    const skeleton = section.querySelector('.cart-upsell__skeleton');
    const fallback = section.querySelector('.cart-upsell-fallback');
    if (skeleton) skeleton.style.display = 'none';
    if (fallback) fallback.style.display = 'block';
    section.classList.remove('upsell--loading');
  }

  /**
   * Refresh recommendations
   */
  refreshRecommendations() {
    setTimeout(() => this.initializeSections(), 500);
  }
}

// Initialize the system
window.IntelligentCartUpsell = new IntelligentCartUpsell();
